import type { Filter } from "@/components/layout/types";
import { FilterChip } from "@/components/layout-2/ds-settings";
import type { FilterInstance } from "@/hooks/use-object-list-filters";
import { MetadataService } from "@/services/metadata";
import { humanizeToken } from "@/utils/dialer-datetime";
import { generateAutoFilters } from "@/utils/object-list-generators";
import { DRQBuilder } from "@/utils/drq";
import { useEffect, useState } from "react";
import type { FilterNode, FilterTree } from "@/components/layout-2/filter-tree/types";
import { OPERATOR_LABELS, QUANTIFIER_LABELS, countConditions, formulaSummary, joinValues } from "@/components/layout-2/filter-tree/tree-utils";
import type { DRQ } from "./types";

const OPERATOR_TEXT: Record<string, string> = {
    equals: "=",
    not_equal_to: "is not",
    contains: "contains",
    not_contains: "does not contain",
    starts_with: "starts with",
    ends_with: "ends with",
    gt: ">",
    gte: "≥",
    lt: "<",
    lte: "≤",
    is_empty: "is empty",
    is_not_empty: "is not empty",
    in: "is one of",
    not_in: "is not one of"
};

/** Filter definitions of an object, read from its OPTIONS metadata like the standard list does. Empty until loaded. */
export function useFilterDefinitions(objectUrl: string | undefined): Filter[] {
    const [definitions, setDefinitions] = useState<Filter[]>([]);
    useEffect(() => {
        if (!objectUrl) return;
        let cancelled = false;
        MetadataService.get(`/crm/objects/${objectUrl}/`)
            .then(metadata => !cancelled && setDefinitions(generateAutoFilters(metadata)))
            .catch(() => !cancelled && setDefinitions([]));
        return () => {
            cancelled = true;
        };
    }, [objectUrl]);
    return definitions;
}

export const toInstances = (drq: DRQ | undefined): FilterInstance[] => (drq && Object.keys(drq).length > 0 ? DRQBuilder.extractFilterInstancesFromDRQ(drq) : []);

/** A filter as `{label, value}`: the field, then its condition. */
export function describeInstance(instance: FilterInstance, definitions: Filter[]): { label: string; value: string } {
    const definition = definitions.find(item => item.field_name === instance.field_name);
    const shown = definition?.options?.find(option => option.value === instance.value)?.label ?? instance.value;
    const operator = OPERATOR_TEXT[instance.operator] ?? instance.operator;
    const value = instance.operator === "is_empty" || instance.operator === "is_not_empty" ? operator : `${operator} ${shown}`;
    return { label: definition?.name ?? humanizeToken(instance.field_name), value };
}

const sentence = (instance: FilterInstance, definitions: Filter[]) => {
    const chip = describeInstance(instance, definitions);
    return `${chip.label} ${chip.value}`;
};

export type FilterChange = { kind: "added" | "removed" | "changed"; before?: string; after?: string };

/** Human diff of two filter sets (and of their logic), for the save dialog: a removed and an added filter on the same field read as one change. */
export function diffFilters(saved: FilterInstance[], draft: FilterInstance[], definitions: Filter[], savedLogic: string, draftLogic: string): FilterChange[] {
    const savedText = saved.map(item => sentence(item, definitions));
    const draftText = draft.map(item => sentence(item, definitions));
    const removed = saved.filter((_, index) => !draftText.includes(savedText[index]));
    const changes: FilterChange[] = [];
    draft
        .filter((_, index) => !savedText.includes(draftText[index]))
        .forEach(item => {
            const twin = removed.findIndex(other => other.field_name === item.field_name);
            if (twin >= 0) changes.push({ kind: "changed", before: sentence(removed.splice(twin, 1)[0], definitions), after: sentence(item, definitions) });
            else changes.push({ kind: "added", after: sentence(item, definitions) });
        });
    removed.forEach(item => changes.push({ kind: "removed", before: sentence(item, definitions) }));
    if (savedLogic !== draftLogic) changes.push({ kind: "changed", before: `Logic ${savedLogic || "AND"}`, after: `Logic ${draftLogic || "AND"}` });
    return changes;
}

export const describeFilters = (drq: DRQ | undefined, definitions: Filter[]) => toInstances(drq).map(instance => describeInstance(instance, definitions));

/** Read-only rendering of a filter set, for summaries and for run snapshots. */
export function FilterChips({ filters, definitions }: { filters: DRQ | undefined; definitions: Filter[] }) {
    const chips = describeFilters(filters, definitions);
    if (chips.length === 0) return <span className="text-sm text-gray-600">No filter — every record of the object matches.</span>;
    return (
        <div className="flex flex-wrap items-center gap-1">
            {chips.map((chip, index) => (
                <FilterChip key={index} {...chip} />
            ))}
        </div>
    );
}

const pathLabel = (path: string) => path.split("__").map(humanizeToken).join(" › ");

/** Advanced filters (a filter tree) as chips: one per top-level condition, block or group. */
export function describeTree(tree: FilterTree | null | undefined): Array<{ label: string; value: string }> {
    const one = (node: FilterNode): { label: string; value: string } => {
        if (node.type === "condition") {
            if (node.formula != null) {
                return { label: pathLabel(node.field), value: [node.operator ? OPERATOR_LABELS[node.operator] : "", formulaSummary(node.formula_label || node.formula)].filter(Boolean).join(" ") };
            }
            const values = (node.value ?? []).map(String);
            return { label: pathLabel(node.field), value: [node.operator ? OPERATOR_LABELS[node.operator] : "", joinValues(values)].filter(Boolean).join(" ") };
        }
        if (node.type === "related") {
            const count = countConditions(node.filter);
            return { label: `${QUANTIFIER_LABELS[node.quantifier]} ${pathLabel(node.relation)}`, value: count ? `${count} condition${count > 1 ? "s" : ""}` : "" };
        }
        if (node.type === "group") {
            const count = countConditions(node);
            return { label: node.conjunction === "or" ? "Any of" : "All of", value: `${count} condition${count > 1 ? "s" : ""}` };
        }
        return { label: node.label || "Saved filters", value: "" };
    };
    return (tree?.root.children ?? []).map(one);
}

/** Read-only rendering of advanced filters (a filter tree). */
export function FilterTreeChips({ tree }: { tree: FilterTree }) {
    const chips = describeTree(tree);
    if (chips.length === 0) return <span className="text-sm text-gray-600">No filter — every record of the object matches.</span>;
    return (
        <div className="flex flex-wrap items-center gap-1">
            {chips.map((chip, index) => (
                <FilterChip key={index} {...chip} />
            ))}
        </div>
    );
}
